import { AxiosInstance, AxiosRequestConfig } from 'axios';

import helper from './helper';
import {
  CategorySku,
  DistributorSku,
  GroupSku,
  PriceListSku,
  PriceSku,
  Sku,
  SkuGroupLink,
  SkuPhoto,
  SubcategorySku,
  UnitSku,
} from './models/sku';

const ENDPOINT = 'sku/';

export type SkuListPayload = {
  limit: number;
  offset: number;
  sort: string;
  filter?: {
    pid?: number;
    code?: string;
    erp_code?: string;
    uniq_code?: string;
    bar_code?: string;
    ean_code?: string;
    trade_mark?: string;
    brand?: string;
    title?: string;
    title_en?: string;
    category_id?: number[];
    subcategory_id?: number[];
    unit_id?: number[];
    is_available_for_order?: boolean;
    is_top?: boolean;
    created_dt?: string[];
    updated_dt?: string[];
    status?: string;
    is_active?: boolean;
  };
};

export type SkuMutationPayload = {
  code?: string;
  erp_code?: string;
  uniq_code?: string;
  bar_code?: string;
  ean_code?: string;
  trade_mark?: string;
  brand: string;
  title: string;
  title_en?: string;
  category_id: number;
  subcategory_id: number;
  material?: string;
  taste?: string;
  weight?: number;
  volume?: number;
  unit_id: number;
  number_in_package: number;
  is_available_for_order: boolean;
  is_top: boolean;
  sort_order: number;
  status: 'active' | 'inactive';
  is_active: boolean;
};

export type SkuCreatePayload = SkuMutationPayload;
export type SkuUpdatePayload = SkuMutationPayload & { pid: number };

export type DeletePayload = { id: number; is_active: boolean };

export type CategorySkuCreatePayload = { name: string; is_active: boolean };
export type CategorySkuUpdatePayload = { id: number; name: string; is_active: boolean };

export type SubcategorySkuCreatePayload = { name: string; category_id: number; is_active: boolean };
export type SubcategorySkuUpdatePayload = { id: number; name: string; category_id: number; is_active: boolean };

export type GroupSkuCreatePayload = { title: string; group_id?: number; is_mobile: boolean; is_active: boolean };
export type GroupSkuUpdatePayload = { id: number; title: string; group_id: number; is_mobile: boolean; is_active: boolean };

export type DistributorSkuMutationPayload = {
  distributor_id: number;
  distributor_sku_code: string;
  distributor_sku_title: string;
  distributor_bar_code?: string;
  distributor_ean_code?: string;
  distributor_unit?: string;
  title_match_percentage?: number;
  sku_id?: number | null;
  is_active?: boolean;
};
export type DistributorSkuCreatePayload = DistributorSkuMutationPayload;
export type DistributorSkuUpdatePayload = DistributorSkuMutationPayload & { id: number };
export type DistributorSkuDeletePayload = { id: number; is_active: boolean };

export type DistributorSkuListParams = {
  is_active?: boolean;
  distributor_id?: number | string;
  /** true — sku_id != null, false — sku_id == null */
  is_matched?: boolean;
  distributor_sku_code?: string;
  distributor_sku_title?: string;
  sku_code?: string;
  sku_title?: string;
  limit?: number;
  offset?: number;
  cacheForce?: boolean;
};

export type UnitSkuCreatePayload = { name: string; is_active: boolean };
export type UnitSkuUpdatePayload = { id: number; name: string; is_active: boolean };

export type SkuGroupLinkPayload = { sku_id: number; group_id: number; is_active: boolean };

export type SkuPhotoCreatePayload = { file_id: number; sku_id: number; is_active?: boolean };
export type SkuPhotoDeletePayload = { file_id: number; sku_id: number; is_active: boolean };

export type PriceListsPayload = {
  distributor_id?: number;
  outlet_id?: number;
  status?: string;
  last_update?: string;
  limit?: number;
  offset?: number;
};

export type PricesPayload = {
  sku_id?: number;
  sku_ids?: string;
  limit?: number;
  offset?: number;
};

// eslint-disable-next-line import/no-anonymous-default-export
export default (instance: AxiosInstance, config: AxiosRequestConfig) => ({
  // SKU
  getSkus: (payload: SkuListPayload) =>
    instance.post<ApiResponse<Paginated<'data', Sku>>>(ENDPOINT + 'sku/list/', payload, config),
  createSku: (payload: SkuCreatePayload) =>
    instance.post<ApiResponse<{ id: number }>>(ENDPOINT + 'sku/create/', payload, config),
  updateSku: (payload: SkuUpdatePayload) =>
    instance.put<ApiResponse<{ id: number }>>(ENDPOINT + 'sku/update/', payload, config),
  deleteSku: (payload: DeletePayload) =>
    instance.post<ApiResponse<{ id: number }>>(ENDPOINT + 'sku/delete/', payload, config),

  // CATEGORY
  getCategories: (is_active?: boolean) =>
    instance.get<ApiResponse<CategorySku[]>>(
      ENDPOINT + 'category_sku/list/' + helper.toQueryString({ is_active }), config),
  createCategory: (payload: CategorySkuCreatePayload) =>
    instance.post<ApiResponse<{ id: number }>>(ENDPOINT + 'category_sku/create/', payload, config),
  updateCategory: (payload: CategorySkuUpdatePayload) =>
    instance.put<ApiResponse<{ id: number }>>(ENDPOINT + 'category_sku/update/', payload, config),
  deleteCategory: (payload: DeletePayload) =>
    instance.post<ApiResponse<{ id: number }>>(ENDPOINT + 'category_sku/delete/', payload, config),

  // SUBCATEGORY
  getSubcategories: (is_active?: boolean) =>
    instance.get<ApiResponse<SubcategorySku[]>>(
      ENDPOINT + 'subcategory_sku/list/' + helper.toQueryString({ is_active }), config),
  createSubcategory: (payload: SubcategorySkuCreatePayload) =>
    instance.post<ApiResponse<{ id: number }>>(ENDPOINT + 'subcategory_sku/create/', payload, config),
  updateSubcategory: (payload: SubcategorySkuUpdatePayload) =>
    instance.put<ApiResponse<{ id: number }>>(ENDPOINT + 'subcategory_sku/update/', payload, config),
  deleteSubcategory: (payload: DeletePayload) =>
    instance.post<ApiResponse<{ id: number }>>(ENDPOINT + 'subcategory_sku/delete/', payload, config),

  // GROUP
  getGroups: (params?: { is_active?: boolean; is_mobile?: boolean }) =>
    instance.get<ApiResponse<GroupSku[]>>(
      ENDPOINT + 'group_sku/list/' + helper.toQueryString(params), config),
  createGroup: (payload: GroupSkuCreatePayload) =>
    instance.post<ApiResponse<{ id: number }>>(ENDPOINT + 'group_sku/create/', payload, config),
  updateGroup: (payload: GroupSkuUpdatePayload) =>
    instance.put<ApiResponse<{ id: number }>>(ENDPOINT + 'group_sku/update/', payload, config),
  deleteGroup: (payload: DeletePayload) =>
    instance.post<ApiResponse<{ id: number }>>(ENDPOINT + 'group_sku/delete/', payload, config),

  // DISTRIBUTOR SKU
  getDistributorSkus: (params?: DistributorSkuListParams) =>
    instance.get<ApiResponse<Paginated<'data', DistributorSku>>>(
      ENDPOINT + 'distributor_sku/list/' + helper.toQueryString({
        is_active: params?.is_active,
        distributor_id: params?.distributor_id,
        is_matched: params?.is_matched,
        distributor_sku_code: params?.distributor_sku_code,
        distributor_sku_title: params?.distributor_sku_title,
        sku_code: params?.sku_code,
        sku_title: params?.sku_title,
        limit: params?.limit,
        offset: params?.offset,
      }),
      {
        ...config,
        ...(params?.cacheForce !== undefined ? {
          headers: {
            ...config.headers,
            'cache-force': String(params.cacheForce),
          },
        } : {}),
      }),
  createDistributorSku: (payload: DistributorSkuCreatePayload) =>
    instance.post<ApiResponse<{ id: number }>>(ENDPOINT + 'distributor_sku/create/', payload, config),
  updateDistributorSku: (payload: DistributorSkuUpdatePayload) =>
    instance.put<ApiResponse<{ id: number }>>(ENDPOINT + 'distributor_sku/update/', payload, config),
  deleteDistributorSku: (payload: DistributorSkuDeletePayload) =>
    instance.post<ApiResponse<{ id: number }>>(ENDPOINT + 'distributor_sku/delete/', payload, config),

  // UNIT
  getUnits: (is_active?: boolean) =>
    instance.get<ApiResponse<UnitSku[]>>(
      ENDPOINT + 'unit_sku/list/' + helper.toQueryString({ is_active }), config),
  createUnit: (payload: UnitSkuCreatePayload) =>
    instance.post<ApiResponse<{ id: number }>>(ENDPOINT + 'unit_sku/create/', payload, config),
  updateUnit: (payload: UnitSkuUpdatePayload) =>
    instance.put<ApiResponse<{ id: number }>>(ENDPOINT + 'unit_sku/update/', payload, config),
  deleteUnit: (payload: DeletePayload) =>
    instance.post<ApiResponse<{ id: number }>>(ENDPOINT + 'unit_sku/delete/', payload, config),

  // SKU <-> GROUP LINK
  getSkuGroupLinks: (is_active?: boolean) =>
    instance.get<ApiResponse<SkuGroupLink[]>>(
      ENDPOINT + 'sku_group_link/list/' + helper.toQueryString({ is_active }), config),
  createSkuGroupLink: (payload: SkuGroupLinkPayload) =>
    instance.post<ApiResponse<{ id: number }>>(ENDPOINT + 'sku_group_link/create/', payload, config),
  deleteSkuGroupLink: (payload: SkuGroupLinkPayload) =>
    instance.post<ApiResponse<{ id: number }>>(ENDPOINT + 'sku_group_link/delete/', payload, config),

  // SKU PHOTO
  getSkuPhotos: (is_active?: boolean) =>
    instance.get<ApiResponse<SkuPhoto[]>>(
      ENDPOINT + 'sku_photo/list/' + helper.toQueryString({ is_active }), {
        ...config,
        headers: {
          ...config.headers,
          'cache-force': String(true),
        },
      }),
  createSkuPhoto: (payload: SkuPhotoCreatePayload) =>
    instance.post<ApiResponse<{ file_id: number; sku_id: number }>>(ENDPOINT + 'sku_photo/create/', payload, config),
  deleteSkuPhoto: (payload: SkuPhotoDeletePayload) =>
    instance.post<ApiResponse<{ file_id: number; sku_id: number }>>(ENDPOINT + 'sku_photo/delete/', payload, config),

  // PRICE LISTS
  getPriceLists: (params?: PriceListsPayload) =>
    instance.get<ApiResponse<{ items: PriceListSku[]; total: number }>>(
      ENDPOINT + 'pricelists/' + helper.toQueryString(params), config),
  getPrices: (pricelistId: number, params?: PricesPayload) =>
    instance.get<ApiResponse<{ items: PriceSku[]; total: number }>>(
      ENDPOINT + `pricelists/${pricelistId}/prices/` + helper.toQueryString(params), config),
});
